import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { pickBy } from 'lodash-es';
import { BehaviorSubject, debounceTime, map, Subject, switchMap } from 'rxjs';

import { FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  getDefaultVisualEffectDataForType,
  VISUAL_EFFECT_OPTIONS,
  VisualEffectType,
} from '@blaze-shared/elements-domain';
import { BlazeCheckboxChange, BlazeFormValidators } from '@blaze-shared/shared-ui';
import { isNullish, SelectOption, toInt } from '@blaze-shared/shared-util';
import { FontsService } from '@blaze-shared/theming-data-access';
import { type AppTheme } from '@blaze-shared/theming-domain';

import { VisualEffectConfigDialogComponent } from '../visual-effect-config-dialog/visual-effect-config-dialog.component';

const placeholderThemeKeys = [
  'phLogoSideFileName',
  'phLogoSideWidth',
  'phLogoSideHeight',
  'phLogoTopFileName',
  'phLogoTopWidth',
  'phLogoTopHeight',
];

@UntilDestroy()
@Component({
  selector: 'blaze-theme-editor-form',
  templateUrl: './theme-editor-form.component.html',
  styleUrls: ['./theme-editor-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ThemeEditorFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);
  private readonly fontsService = inject(FontsService);

  @Input() theme!: AppTheme;
  @Input() fonts: SelectOption<string, string>[] = [];

  @Output() themeChange = new EventEmitter<AppTheme>();

  private readonly headerFontWeightOptions$$ = new BehaviorSubject<
    SelectOption<FontWeightOption>[]
  >([]);
  private readonly minFontSize = 8;
  private readonly minHeaderFontSize = 10;
  private readonly minBorderRadius = 0;
  private readonly minLineHeight = 8;
  private readonly FORM_DEBOUNCE_TIME = 500;
  private readonly fontSizeCtrl: AbstractControl;
  private readonly lineHeightCtrl: AbstractControl;
  private readonly validators = [
    {
      getControl: (): AbstractControl<any, any> | null => this.fontSizeCtrl,
      update: (theme: AppTheme): string => (theme.value.fontSize = this.minFontSize + 'px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.form.get('radius'),
      update: (theme: AppTheme): string => (theme.value.radius = this.minBorderRadius + 'px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.lineHeightCtrl,
      update: (theme: AppTheme): string => (theme.value.lineHeight = this.minLineHeight + 'px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.form.get('headerFontSize'),
      update: (theme: AppTheme): string =>
        (theme.value.headerFontSize = this.minHeaderFontSize + 'px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.form.get('spacingExternal'),
      update: (theme: AppTheme): string => (theme.value.spacingExternal = '16px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.form.get('spacingInternal'),
      update: (theme: AppTheme): string => (theme.value.spacingInternal = '16px'),
    },
    {
      getControl: (): AbstractControl<any, any> | null => this.form.get('containerOutlineWidth'),
      update: (theme: AppTheme): string => (theme.value.containerOutlineWidth = '1px'),
    },
  ];

  form: FormGroup;
  formId = crypto.randomUUID();
  effects = VISUAL_EFFECT_OPTIONS;
  visualEffectDialogRef: MatDialogRef<any> | undefined;

  headerFontWeightOptions$ = this.headerFontWeightOptions$$.asObservable();

  set containerOutlineFormValue(value: string) {
    this.form.controls.containerOutlineWidth.setValue(this.removeUnitForNumericValue(value, 'px'));
  }
  get containerOutlineFormValue(): string {
    return this.form.value.containerOutlineWidth;
  }

  get paletteControls(): FormControl[] {
    const formArray = this.form.get('palette.bg') as FormArray;
    return formArray.controls as FormControl[];
  }

  get visualEffectConfigDialogBlocked(): boolean {
    const effectType = this.form.value.effectType;
    return !effectType || effectType === 'none';
  }

  get isContainerOutlineEnabled(): boolean {
    return this.form.value.containerOutlineType === 'solid';
  }

  constructor() {
    this.form = this.buildThemeEditorForm();

    this.fontSizeCtrl = this.form.controls['fontSize'];
    this.lineHeightCtrl = this.form.controls['lineHeight'];

    this.observeSupportedFontWeights(
      this.form.controls.headerFontFamily,
      this.form.controls.headerFontWeight,
      this.headerFontWeightOptions$$,
    );

    this.validateTextProperties();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.theme) {
      this.updateFormValues();
    }
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(debounceTime(this.FORM_DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((value) => {
        const isSpacingDefined = !!this.theme.value.spacing;
        const placeholderData = pickBy(value, (v, k) => placeholderThemeKeys.includes(k) && !!v);
        const theme = {
          ...this.theme,
          value: {
            canvas: value.canvas,
            fill: value.fill,
            value: value.value,
            fontFamily: value.fontFamily,
            lineHeight: this.addUnitForNumericValue(value.lineHeight, 'px') as any,
            radius: this.addUnitForNumericValue(value.radius, 'px') as any,
            fontSize: this.addUnitForNumericValue(value.fontSize, 'px') as any,
            complementary: value.complementary,
            title: value.title,
            outline: value.outline,
            accent: value.accent,
            accentLabel: value.accentLabel,
            headerFontSize: this.addUnitForNumericValue(value.headerFontSize, 'px') as any,
            headerFontFamily: value.headerFontFamily,
            headerFontWeight: value.headerFontWeight,
            spacing: this.theme.value.spacing,
            spacingExternal: isSpacingDefined
              ? this.addUnitForNumericValue(value.spacingExternal, 'px')
              : undefined,
            spacingInternal: isSpacingDefined
              ? this.addUnitForNumericValue(value.spacingInternal, 'px')
              : undefined,
            palettes: {
              ...this.theme.value.palettes,
              palette2: value.palette,
            },
            effectType: value.effectType,
            effectX: value.effectX.toString(),
            effectY: value.effectY.toString(),
            effectBlur: value.effectBlur.toString(),
            effectColor1: value.effectColor1,
            containerOutlineType: value.containerOutlineType,
            containerOutlineWidth: this.addUnitForNumericValue(value.containerOutlineWidth, 'px'),
            ...placeholderData,
          },
        };

        this.validateAndUpdate(theme);
        this.themeChange.emit(theme);
      });
  }

  ngOnDestroy(): void {
    this.visualEffectDialogRef?.close();
  }

  showVisualEffectConfigDialog(): void {
    this.visualEffectDialogRef = this.dialog.open(VisualEffectConfigDialogComponent, {
      data: this.form,
      width: '300px',
      panelClass: ['bd', 'bd-dialog', 'bd-dialog-shadow', 'bd-dialog-panel'],
    });
  }

  toggleContainerOutline(event: BlazeCheckboxChange): void {
    if (!event.checked) {
      this.form.patchValue({
        containerOutlineType: undefined,
        containerOutlineWidth: undefined,
      });
      return;
    }

    const iContainerOutlineWidth = Math.max(toInt(this.form.value.containerOutlineWidth, 1));
    this.form.patchValue({
      containerOutlineType: 'solid',
      containerOutlineWidth: `${iContainerOutlineWidth}`,
    });
  }

  private updateFormValues(theme?: AppTheme): void {
    theme ??= this.theme;
    const fullSpacing = Math.max(toInt(theme.value.spacing?.replace('px', '').trim() ?? '0'), 0);
    const containerOutlineType =
      theme.value.containerOutlineType === 'none' ? undefined : theme.value.containerOutlineType;
    const containerOutlineWidth = !containerOutlineType
      ? '1'
      : this.removeUnitForNumericValue(theme.value.containerOutlineWidth ?? '1', 'px');

    this.form.patchValue({
      ...theme.value,
      canvas: theme.value.canvas ?? '#FFFFFF',
      lineHeight: this.removeUnitForNumericValue(theme.value.lineHeight, 'px'),
      radius: this.removeUnitForNumericValue(theme.value.radius, 'px'),
      fontSize: this.removeUnitForNumericValue(theme.value.fontSize, 'px'),
      palette: {
        fg: theme.value.palettes.palette2.fg,
        bg: theme.value.palettes.palette2.bg,
      },
      fontFamily: theme.value.fontFamily,
      headerFontSize: this.removeUnitForNumericValue(theme.value.headerFontSize, 'px'),
      headerFontWeight: theme.value.headerFontWeight,
      spacingExternal:
        this.removeUnitForNumericValue(theme.value.spacingExternal, 'px') ?? fullSpacing,
      spacingInternal:
        this.removeUnitForNumericValue(theme.value.spacingInternal, 'px') ?? fullSpacing,
      containerOutlineType,
      containerOutlineWidth,
    });

    this.cdr.markForCheck();
  }

  private buildThemeEditorForm(): FormGroup {
    const form = this.fb.group({
      fontFamily: [undefined],
      fontSize: [
        10,
        [BlazeFormValidators.number, Validators.min(this.minFontSize), Validators.max(72)],
      ],
      radius: [
        undefined,
        [BlazeFormValidators.number, Validators.min(this.minBorderRadius), Validators.max(25)],
      ],
      lineHeight: [
        undefined,
        [BlazeFormValidators.number, Validators.min(this.minLineHeight), Validators.max(100)],
      ],
      canvas: [undefined],
      fill: [undefined],
      outline: [undefined],
      title: [undefined],
      value: [undefined],
      accent: [undefined],
      accentLabel: [undefined],
      complementary: [undefined],
      headerFontFamily: [undefined],
      headerFontSize: [
        20,
        [BlazeFormValidators.number, Validators.min(this.minHeaderFontSize), Validators.max(72)],
      ],
      headerFontWeight: [500],
      spacingExternal: [
        undefined,
        [BlazeFormValidators.number, Validators.min(0), Validators.max(128)],
      ],
      spacingInternal: [
        undefined,
        [BlazeFormValidators.number, Validators.min(0), Validators.max(128)],
      ],
      palette: this.fb.group({
        fg: [undefined],
        bg: this.fb.array(new Array(10).fill(null).map(() => this.fb.control(undefined))),
      }),
      effectType: 'none',
      effectX: '0',
      effectY: '0',
      effectBlur: '0',
      effectColor1: 'rgba(0,0,0,0.1)',
      containerOutlineType: [undefined],
      containerOutlineWidth: [
        undefined,
        [BlazeFormValidators.number, Validators.min(1), Validators.max(10)],
      ],
      phLogoSideFileName: [undefined],
      phLogoSideWidth: [undefined],
      phLogoSideHeight: [undefined],
      phLogoTopFileName: [undefined],
      phLogoTopWidth: [undefined],
      phLogoTopHeight: [undefined],
    });

    // Update properties when choosing different effects
    form.controls.effectType.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const effectType = (value ?? 'none') as VisualEffectType;
      const defaults = getDefaultVisualEffectDataForType(effectType);
      form.patchValue({
        effectX: defaults?.x ?? '0',
        effectY: defaults?.y ?? '0',
        effectBlur: defaults?.blur ?? '0',
        effectColor1: defaults?.color1 ?? 'rgba(0,0,0,0.1)',
      });
      if (effectType === 'none') {
        this.visualEffectDialogRef?.close();
      }
    });

    return form;
  }

  private validateAndUpdate(theme: AppTheme): void {
    let isValid = true;

    for (const validator of this.validators) {
      const control = validator.getControl();
      if (control && !control.valid) {
        validator.update(theme);
        isValid = false;
      }
    }

    if (!isValid) {
      this.updateFormValues(theme);
    }
  }

  private validateTextProperties(): void {
    this.fontSizeCtrl.valueChanges
      .pipe(debounceTime(this.FORM_DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((value) => {
        if (+value > +this.lineHeightCtrl.value) {
          this.lineHeightCtrl.patchValue(value, { emitEvent: false });
        }
      });

    this.lineHeightCtrl.valueChanges
      .pipe(debounceTime(this.FORM_DEBOUNCE_TIME), untilDestroyed(this))
      .subscribe((value) => {
        if (+value < +this.fontSizeCtrl.value) {
          this.fontSizeCtrl.patchValue(value, { emitEvent: false });
        }
      });
  }

  private addUnitForNumericValue(
    value: string | number | null | undefined,
    unit: string,
  ): string | undefined {
    if (isNullish(value)) {
      return undefined;
    }

    const iValue = typeof value === 'number' ? value : toInt(value);
    return `${iValue}${unit}`;
  }

  private removeUnitForNumericValue(
    value: string | number | null | undefined,
    unit: string,
  ): string | undefined {
    if (isNullish(value)) {
      return undefined;
    }

    if (typeof value === 'number') {
      return `${value}`;
    }

    return value.replace(unit, '').trim();
  }

  /**
   * Gets supported font weight options for a selected font family
   */
  private observeSupportedFontWeights(
    fontFamilyCtrl: AbstractControl<string | undefined> | undefined,
    fontWeightCtrl: AbstractControl<FontWeightOption | undefined> | undefined,
    target: Subject<SelectOption<FontWeightOption>[]>,
  ): void {
    if (!fontFamilyCtrl || !fontWeightCtrl) {
      return;
    }

    fontFamilyCtrl.valueChanges
      .pipe(
        untilDestroyed(this),
        switchMap((fontFamily) => {
          return this.fontsService.getFontWeightOptions(fontFamily).pipe(
            map((options) => ({
              fontFamily,
              options,
            })),
          );
        }),
      )
      .subscribe(({ fontFamily, options }) => {
        target.next(options);

        // When font weight control has a set value that is not supported, use the lowest possible setting.
        const currentFontWeightValue = fontWeightCtrl.value;
        const validated = this.fontsService.getFontWeight(fontFamily, currentFontWeightValue);

        if (currentFontWeightValue !== validated) {
          fontWeightCtrl.patchValue(validated);
        }
      });
  }
}
